import { getDiagnosticsAnalyticsList } from "../../../../Api/BmsFaultsApi";
import { formatDateForAPI } from "../../../../Helper/Analytics/AnalyticsHelper";
import { formatDateRangeWithSmartYear } from "../../../../Helper/DatePicker/DateFormatters";

/**
 * Common API call pattern for fetching table data
 * @param {Object} globalFilters - hierarchicalLocation and alertCategories
 * @param {Object} params - API parameters
 * @returns {Promise<{data: Array, total: number}>}
 */
export const getTableDataFromApi = async (globalFilters, params = {}) => {
  const listResponse = await getDiagnosticsAnalyticsList(globalFilters, params);
  const listData = listResponse?.response?.data || [];
  const total = listResponse?.response?.total?.total || listData.length;

  const transformedData = listData.map((item, index) => {
    let row = { "xLabel": item.xLabel, "id": item.xLabel || index, ...item.metaInfo || {} };
    for (const metric of item.metricsArray || []) {
      row[metric.type] = metric.value || 0;
    }
    return row;
  });

  return { data: transformedData, total };
};

/**
 * Build base API parameters for drill-down queries
 * @param {Object} dataPoint - Selected data point with startDate/endDate
 * @param {Date} startDate - Fallback start date
 * @param {Date} endDate - Fallback end date
 * @param {Array} groupBy - Group by fields
 * @param {Array} select - Select fields
 * @param {Array} metaInfo - Meta info fields
 * @param {Array} filters - Filter array
 * @param {Object} additionalParams - Any additional parameters
 * @returns {Object} Base API params
 */
export const buildBaseApiParams = (
  dataPoint,
  startDate,
  endDate,
  groupBy,
  select,
  metaInfo,
  filters = [],
  additionalParams = {}
) => {
  return {
    startDate: formatDateForAPI(dataPoint?.startDate || startDate),
    endDate: formatDateForAPI(dataPoint?.endDate || endDate),
    groupBy,
    granularity: null,
    select,
    metaInfo,
    filters,
    skipPagination: false,
    pageNum: additionalParams.pageNum || 1,
    pageCount: additionalParams.pageCount || 10,
    ...additionalParams,
  };
};

/**
 * Build API parameters for table actions (download, email, etc.)
 * @param {Object} baseParams - Base API parameters
 * @param {string} actionType - Action type (download, mail, etc.)
 * @param {Array} rowSelectionKeys - Selected row keys
 * @param {boolean} selectAll - Whether all rows are selected
 * @param {Object} params - Additional action parameters
 * @returns {Object} Action API params
 */
export const buildActionApiParams = (
  baseParams,
  actionType,
  rowSelectionKeys,
  selectAll,
  params = {}
) => {
  const apiParams = {
    actionType,
    ...baseParams,
    resourceIDs: selectAll ? [] : rowSelectionKeys,
    selectAll,
    columns: params.columns || [],
    columnOrder: params.columnOrder || [],
  };

  // Add email params if action is mail
  if (actionType === "mail") {
    apiParams.emailTo = params.emailTo;
    apiParams.emailCc = params.emailCc;
    apiParams.subject = params.subject;
    apiParams.body = params.body;
  }

  return apiParams;
};

/**
 * Format CSV filename with date range
 * @param {string} prefix - File prefix
 * @param {Array} parts - Additional parts to include in filename
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {string} Formatted filename
 */
export const formatCSVFileName = (prefix, parts = [], startDate, endDate) => {
  const formattedStartDate = new Date(startDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const formattedEndDate = new Date(endDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  const partsStr = parts.length > 0 ? `_${parts.join('_')}` : '';
  return `${prefix}${partsStr}_${formattedStartDate}-${formattedEndDate}.csv`;
};

/**
 * Build date category string for display
 * @param {Object} selectedDataPoint - Selected data point with startDate/endDate
 * @param {Date} startDate - Fallback start date
 * @param {Date} endDate - Fallback end date
 * @returns {string} Formatted date range
 */
export const buildDateCategory = (selectedDataPoint, startDate, endDate) => {
  return formatDateRangeWithSmartYear(
    selectedDataPoint?.startDate || startDate,
    selectedDataPoint?.endDate || endDate
  );
};
